import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ReactElement, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { Visibility as VisibilityIcon } from '@mui/icons-material'
import { useAppSelector } from 'src/store/hooks'
import {
  selectAssignmentUserProgressV4,
  selectGroupsWithDefaults,
  AssignmentDetailV3,
  AssignmentCreditsResponse,
} from 'src/v3/manage/manage.slice'
import { CorrectIcon } from 'src/v3/core/icons'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { CREDIT_SOURCE_CONFIG } from '../../constants'
import UserFilters from '../../components/user-filters'
import { dataGridSx } from '../../styles'
import { getProgressColor, getIsOverdue } from '../utils/helpers'

dayjs.extend(utc)

type UserProgressCardProps = {
  assignment: AssignmentDetailV3 | null
  isLoading: boolean
  isSubmitting: boolean
  credits: AssignmentCreditsResponse | null
  onViewAssignmentProgress: (userID: string) => void
  onGiveBulkCredit: (userIDs: string[]) => void
}

function UserProgressCardSkeleton(): ReactElement {
  return (
    <Card sx={{ bgcolor: 'background.paper' }} className="p-6">
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <Skeleton variant="text" width={180} height={32} />
        <Skeleton variant="rectangular" width={120} height={32} />
      </Box>

      <Box className="grid grid-cols-1 gap-6 mb-4 md:grid-cols-2">
        <Skeleton variant="rectangular" width="100%" height={56} />
        <Skeleton variant="rectangular" width="100%" height={56} />
      </Box>

      <Box className="w-full" sx={{ height: 400 }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    </Card>
  )
}

function formatStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'Completed'
    case 'InProgress':
      return 'In Progress'
    case 'NotStarted':
      return 'Not Started'
    default:
      return status
  }
}

export default function UserProgressCard({
  assignment,
  isLoading,
  isSubmitting,
  credits,
  onViewAssignmentProgress,
  onGiveBulkCredit,
}: UserProgressCardProps): ReactElement {
  let [searchQuery, setSearchQuery] = useState('')
  let [groupFilter, setGroupFilter] = useState('')
  let [selectedUserIDs, setSelectedUserIDs] = useState<string[]>([])

  let linkedServiceID = assignment?.linkedServiceID || ''
  let userProgress = useAppSelector(selectAssignmentUserProgressV4)
  let userGroups = useAppSelector(selectGroupsWithDefaults(linkedServiceID))
  let isOverdue = getIsOverdue(assignment?.dueDate || '')
  let assignmentCreditsByUser = useMemo(
    () =>
      new Map(
        (credits?.assignmentCredits ?? []).map((c) => [c.userID, c.source]),
      ),
    [credits],
  )

  let gridColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'userName',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'progressPercentage',
        headerName: 'Progress',
        width: 150,
        type: 'number',
        headerAlign: 'left',
        align: 'center',
        valueFormatter: (value) =>
          value != null ? `${Math.round(value)}%` : '0%',
        renderCell: (params) => {
          let progressColor = getProgressColor(params.value, isOverdue)

          return (
            <Box
              className="flex flex-col items-center justify-center"
              sx={{ height: '100%' }}
            >
              <Box component="span" sx={{ mb: 0.5, color: progressColor }}>
                {Math.round(params.value)}%
              </Box>
              <LinearProgress
                variant="determinate"
                value={params.value}
                sx={{ height: 6, borderRadius: 3, minWidth: '100%' }}
                color={progressColor}
              />
            </Box>
          )
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        valueFormatter: (value) => (value ? formatStatus(value) : 'Unknown'),
        renderCell: (params) => (
          <Box className="flex items-center" sx={{ height: '100%' }}>
            <Chip
              size="medium"
              label={formatStatus(params.value)}
              color={
                params.value === 'Completed'
                  ? 'success'
                  : params.value === 'InProgress'
                    ? 'primary'
                    : 'default'
              }
            />
          </Box>
        ),
      },
      {
        field: 'courseProgress',
        headerName: 'Courses',
        width: 120,
        valueGetter: (params: any) =>
          params?.row
            ? `${params.row.completedCourses}/${params.row.totalCourses}`
            : '0/0',
        renderCell: (params) => (
          <span>
            {params.row.completedCourses}/{params.row.totalCourses}
          </span>
        ),
        sortComparator: (v1, v2, param1: any, param2: any) => {
          // Sort by completion ratio
          let ratio1 = param1?.row
            ? param1.row.completedCourses / param1.row.totalCourses
            : 0
          let ratio2 = param2?.row
            ? param2.row.completedCourses / param2.row.totalCourses
            : 0
          return ratio1 - ratio2
        },
      },
      {
        field: 'lastActivityDate',
        headerName: 'Last Activity',
        width: 150,
        valueFormatter: (value) => {
          if (!value) return 'Never'
          return dayjs(value).format('MMM D, YYYY h:mm A')
        },
        renderCell: (params) => (
          <span>{params.value ? dayjs(params.value).fromNow() : 'Never'}</span>
        ),
      },
      {
        field: 'completionDate',
        headerName: 'Completed',
        width: 150,
        valueFormatter: (value) =>
          !value ? 'Incomplete' : dayjs.utc(value).local().format('MM/DD/YYYY'),
        renderCell: (params) => (
          <span>
            {!params.value
              ? 'Incomplete'
              : dayjs.utc(params.value).local().format('MM/DD/YYYY')}
          </span>
        ),
      },
      {
        field: 'creditSource',
        headerName: 'Credit Source',
        width: 160,
        renderCell: (params) => {
          let source = assignmentCreditsByUser.get(params.row.userID)
          if (!source) return null
          let config = CREDIT_SOURCE_CONFIG[source]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
        sortable: false,
      },
      {
        field: 'progressDetails',
        headerName: 'Progress Details',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Box
            className="flex items-center justify-end"
            sx={{
              height: '100%',
            }}
          >
            <Tooltip title="View Courses">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  onViewAssignmentProgress(params.row.userID)
                }}
                color="primary"
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        sortable: false,
        filterable: false,
        disableExport: true,
      },
    ],
    [isOverdue, onViewAssignmentProgress, assignmentCreditsByUser],
  )

  let UserProgressToolbar = useMemo(
    () => () => (
      <Box sx={{ backgroundColor: 'background.paperDark' }}>
        <DataGridToolbar
          fileName="assignment-user-progress"
          showQuickFilter={false}
        />
      </Box>
    ),
    [],
  )

  let filterGroup = useMemo(
    () => userGroups.find((group) => group.id === groupFilter),
    [userGroups, groupFilter],
  )

  // Filter groups to only show those with users in the user progress list
  let groupsWithUsers = useMemo(() => {
    let userProgressUserIDs = new Set(userProgress.map((u) => u.userID))
    return userGroups.filter((group) =>
      group.users?.some((userId) => userProgressUserIDs.has(userId)),
    )
  }, [userGroups, userProgress])

  // Prepare data for the grid with filtering
  let gridRows = useMemo(
    () =>
      userProgress
        .map((user) => ({
          id: user.userID,
          userID: user.userID,
          userName: user.userName,
          email: user.email,
          progressPercentage: user.progressPercentage,
          status: user.status,
          completedCourses: user.completedCourses,
          totalCourses: user.totalCourses,
          lastActivityDate: user.lastActivityDate,
          completionDate: user.completionDate,
        }))
        .filter((row) => {
          if (groupFilter && !filterGroup?.users?.includes(row.userID)) {
            return false
          }

          if (searchQuery) {
            let query = searchQuery.toLowerCase()
            let nameMatch = row.userName.toLowerCase().includes(query)
            let emailMatch = row.email.toLowerCase().includes(query)
            if (!nameMatch && !emailMatch) {
              return false
            }
          }

          return true
        }),
    [userProgress, groupFilter, filterGroup, searchQuery],
  )

  if (isLoading || !assignment) {
    return <UserProgressCardSkeleton />
  }

  return (
    <Card sx={{ bgcolor: 'background.paper' }} className="mb-6">
      <Box className="p-6">
        <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <Box className="mb-4">
            <Typography variant="h6" component="h2" className="mb-2">
              User Completion Status ({userProgress.length} users)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              At-a-glance statistics for individual users' progress in the
              assignment
            </Typography>
          </Box>
          <Box className="flex flex-wrap items-center gap-2">
            <Chip
              label={`${gridRows.length} ${gridRows.length !== 1 ? 'users' : 'user'} shown`}
              size="medium"
              color="primary"
            />
            {(searchQuery || groupFilter) && (
              <Button
                className="ml-2 whitespace-nowrap"
                variant="outlined"
                color="secondary"
                size="medium"
                onClick={() => {
                  setSearchQuery('')
                  setGroupFilter('')
                }}
                sx={{ height: 32 }}
              >
                Reset Filters
              </Button>
            )}
          </Box>
        </Box>

        <UserFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          groupFilter={groupFilter}
          onGroupChange={setGroupFilter}
          groups={groupsWithUsers || []}
        />

        {selectedUserIDs.length > 0 && (
          <Box className="flex items-center justify-end p-4">
            <Chip
              label={
                selectedUserIDs.length === 1
                  ? `${selectedUserIDs.length} user selected`
                  : `${selectedUserIDs.length} users selected`
              }
              variant="outlined"
              color="secondary"
              size="medium"
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              className="ml-4"
              startIcon={<CorrectIcon />}
              onClick={() => onGiveBulkCredit(selectedUserIDs)}
              disabled={isSubmitting}
            >
              Give Assignment Credit
            </Button>
          </Box>
        )}
      </Box>

      <Box className="px-6 pb-6">
        <DataGridPremium
          rows={gridRows}
          columns={gridColumns}
          slots={{ toolbar: UserProgressToolbar }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            density: 'standard',
            pinnedColumns: { right: ['progressDetails'] },
          }}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection={true}
          rowSelectionModel={{
            type: 'include',
            ids: new Set(selectedUserIDs),
          }}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedUserIDs(Array.from(newSelection.ids) as string[])
          }
          sx={dataGridSx}
        />
      </Box>
    </Card>
  )
}
